import React, { useState, useEffect, useContext, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { AxiosAdminInstance } from '../utils';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { Context } from "./authcontext";
import { useParams } from 'react-router-dom';

interface Track {
    id: string;
    name: string;
    day: string;
    sessions: { [key: string]: Session };
    sessionsOrder: string[];
}

interface Session {
    name: string;
    topic: string;
    time: string;
    speakers?: { [key: string]: { speakerid: string; speakername: string; emailid: string; order: number } };
}

const ManageSchedule: React.FC = () => {
    const [tracks, setTracks] = useState<Track[]>([]);
    const [tracksOrder, setTracksOrder] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingTrack, setEditingTrack] = useState<Track | null>(null);
    const [editingSession, setEditingSession] = useState<{ trackId: string; sessionId: string; session: Session } | null>(null);
    const [newTrackName, setNewTrackName] = useState('');
    const [selectedTrackId, setSelectedTrackId] = useState('');
    const [selectedDay, setSelectedDay] = useState('');
    const [newSession, setNewSession] = useState<Session>({ name: '', topic: '', time: '' });
    const [speakers, setSpeakers] = useState<any[]>([]);
    const [selectedSpeakers, setSelectedSpeakers] = useState<any[]>([]);
    const [speakerTableKey, setSpeakerTableKey] = useState(0);
    const [preSelectedSpeakerEmails, setPreSelectedSpeakerEmails] = useState<string[]>([]);
    const [showSpeakerModal, setShowSpeakerModal] = useState(false);
    const [modalSpeakers, setModalSpeakers] = useState<any[]>([]);
    const { user } = useContext(Context);
    const { eventcode } = useParams<{ eventcode: string }>();

    useEffect(() => {
        fetchTracks();
        fetchSpeakers();
    }, []);

    const fetchSpeakers = async () => {
        try {
            const response = await AxiosAdminInstance.get(`/api/getspeakers?eventcode=${eventcode}`);
            if (response.data.result === "Success" && response.data.data?.length > 0) {
                setSpeakers(response.data.data);
            } else {
                setSpeakers([]);
            }
        } catch (error) {
            toast.error('Failed to fetch speakers');
            setSpeakers([]);
        }
    };

    useEffect(() => {
        if (editingTrack) {
            setNewTrackName(editingTrack.name);
            setSelectedDay(editingTrack.day);
        } else {
            setNewTrackName('');
            setSelectedDay('');
        }
    }, [editingTrack]);

    useEffect(() => {
        if (editingSession) {
            setSelectedTrackId(editingSession.trackId);

            setNewSession(editingSession.session);
            // Match speakers from session data with speakers from API based on email
            const currentSpeakers = editingSession.session.speakers ? Object.values(editingSession.session.speakers) : [];
            const matchedSpeakers = currentSpeakers.map((sessionSpeaker: any) => {
                return speakers.find(apiSpeaker => apiSpeaker.mailid === sessionSpeaker.emailid);
            }).filter(speaker => speaker !== undefined);
            setSelectedSpeakers(matchedSpeakers);
            // Set pre-selected emails for DataTable
            const emailArray = matchedSpeakers.map((speaker: any) => speaker.mailid);
            setPreSelectedSpeakerEmails(emailArray);
        } else {
            setSelectedTrackId('');
            console.log('Resetting newSession to default values');
            setNewSession({ name: '', topic: '', time: '' });
            setSelectedSpeakers([]);
            setPreSelectedSpeakerEmails([]);
        }
    }, [editingSession, speakers]);

    const fetchTracks = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTracks', {
                action: 'getTracks',
                data: { eventcode }
            });
            if (response.data.success) {
                const tracksObj = response.data.tracks || {};
                const tracksOrder = response.data.tracksOrder || [];
                const tracksArray = tracksOrder.map((id: string) => {
                    const trackData = tracksObj[id];
                    if (!trackData) return null;
                    return {
                        id,
                        name: trackData.name,
                        day: trackData.day || '',
                        sessions: trackData.sessions || {},
                        sessionsOrder: trackData.sessionsOrder || []
                    };
                }).filter((track): track is Track => track !== null);
                setTracks(tracksArray);
                setTracksOrder(tracksOrder);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to fetch tracks');
        }
        setLoading(false);
    };

    const handleAddTrack = async () => {
        if (!newTrackName.trim()) {
            toast.error('Track name is required');
            return;
        }
        if (!selectedDay.trim()) {
            toast.error('Day is required');
            return;
        }
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTracks', {
                action: editingTrack ? 'editTrack' : 'addTrack',
                data: editingTrack ? { eventcode, trackId: editingTrack.id, name: newTrackName, day: selectedDay } : { eventcode, name: newTrackName, day: selectedDay, sessionsOrder: [] }
            });
            if (response.data.success) {
                toast.success(editingTrack ? 'Track updated successfully' : 'Track added successfully');
                setNewTrackName('');
                setSelectedDay('');
                setEditingTrack(null);
                fetchTracks();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(editingTrack ? 'Failed to update track' : 'Failed to add track');
        }
        setLoading(false);
    };

    const handleCancelTrack = () => {
        setNewTrackName('');
        setEditingTrack(null);
    };

    const handleDeleteTrack = async (trackId: string) => {
        if (!window.confirm('Are you sure you want to delete this track?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTracks', {
                action: 'deleteTrack',
                data: { eventcode, trackId }
            });
            if (response.data.success) {
                toast.success('Track deleted successfully');
                fetchTracks();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete track');
        }
        setLoading(false);
    };

    const handleAddSession = async () => {
        if (!selectedTrackId && !editingSession) {
            toast.error('Please select a track');
            return;
        }
        if (!newSession.name.trim()) {
            toast.error('Session name is required');
            return;
        }
        if (!newSession.topic.trim()) {
            toast.error('Topic is required');
            return;
        }
        if (!newSession.time.trim()) {
            toast.error('Time is required');
            return;
        }
        setLoading(true);
        try {
            const speakersObj: { [key: string]: { speakerid: string; speakername: string; emailid: string; order: number; socialmedia?: any[] } } = {};
            selectedSpeakers.forEach((speaker: any, index: number) => {
                speakersObj[speaker.mailid] = {
                    speakerid: speaker.mailid,
                    speakername: speaker.name,
                    emailid: speaker.mailid,
                    order: index + 1,
                    socialmedia: speaker.socialmedia || []
                };
            });
            const sessionData = { ...newSession, speakers: speakersObj };
            const response = await AxiosAdminInstance.post('/api/admin/manageTracks', {
                action: editingSession ? 'editSession' : 'addSession',
                data: editingSession ? { eventcode, trackId: editingSession.trackId, sessionId: editingSession.sessionId, session: sessionData } : { eventcode, trackId: selectedTrackId, session: sessionData }
            });
            if (response.data.success) {
                toast.success(editingSession ? 'Session updated successfully' : 'Session added successfully');
                setNewSession({ name: '', topic: '', time: '' });
                setSelectedTrackId('');
                setSelectedSpeakers([]);
                setPreSelectedSpeakerEmails([]);
                setSpeakerTableKey(prev => prev + 1); // Force DataTable to reset selection
                setEditingSession(null);
                fetchTracks();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(editingSession ? 'Failed to update session' : 'Failed to add session');
        }
        setLoading(false);
    };

    const handleCancelSession = () => {
        setNewSession({ name: '', topic: '', time: '' });
        setSelectedTrackId('');
        setSelectedSpeakers([]);
        setPreSelectedSpeakerEmails([]);
        setSpeakerTableKey(prev => prev + 1); // Force DataTable to reset selection
        setEditingSession(null);
    };

    const handleDeleteSession = async (trackId: string, sessionId: string) => {
        if (!window.confirm('Are you sure you want to delete this session?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTracks', {
                action: 'deleteSession',
                data: { eventcode, trackId, sessionId }
            });
            if (response.data.success) {
                toast.success('Session deleted successfully');
                fetchTracks();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete session');
        }
        setLoading(false);
    };

    const moveTrackUp = async (index: number) => {
        if (index === 0) return;
        const newOrder = [...tracksOrder];
        [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
        await handleUpdateTrackOrder(newOrder);
    };

    const moveTrackDown = async (index: number) => {
        if (index === tracks.length - 1) return;
        const newOrder = [...tracksOrder];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        await handleUpdateTrackOrder(newOrder);
    };

    const moveSessionUp = async (trackId: string, sessionId: string) => {
        const track = tracks.find(t => t.id === trackId);
        if (!track) return;
        const sessIndex = track.sessionsOrder.indexOf(sessionId);
        if (sessIndex <= 0) return;
        const newOrder = [...track.sessionsOrder];
        [newOrder[sessIndex], newOrder[sessIndex - 1]] = [newOrder[sessIndex - 1], newOrder[sessIndex]];
        await handleUpdateSessionOrder(trackId, newOrder);
    };

    const moveSessionDown = async (trackId: string, sessionId: string) => {
        const track = tracks.find(t => t.id === trackId);
        if (!track) return;
        const sessIndex = track.sessionsOrder.indexOf(sessionId);
        if (sessIndex >= track.sessionsOrder.length - 1) return;
        const newOrder = [...track.sessionsOrder];
        [newOrder[sessIndex], newOrder[sessIndex + 1]] = [newOrder[sessIndex + 1], newOrder[sessIndex]];
        await handleUpdateSessionOrder(trackId, newOrder);
    };

    const handleUpdateTrackOrder = async (newOrder: string[]) => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTracks', {
                action: 'updateTrackOrder',
                data: { eventcode, tracksOrder: newOrder }
            });
            if (response.data.success) {
                toast.success('Track order updated');
                setTracksOrder(newOrder);
                const newTracks = newOrder.map(id => tracks.find(t => t.id === id)).filter(Boolean) as Track[];
                setTracks(newTracks);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to update track order');
        }
        setLoading(false);
    };

    const handleUpdateSessionOrder = async (trackId: string, newOrder: string[]) => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTracks', {
                action: 'updateSessionOrder',
                data: { eventcode, trackId, sessionsOrder: newOrder }
            });
            if (response.data.success) {
                toast.success('Session order updated');
                const newTracks = tracks.map(track => 
                    track.id === trackId ? { ...track, sessionsOrder: newOrder } : track
                );
                setTracks(newTracks);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to update session order');
        }
        setLoading(false);
    };

    const trackColumns = [
        { name: 'Actions', cell: (row: Track, index: number) => (
            <div>
                <button className="btn btn-sm btn-light me-1" onClick={() => moveTrackUp(index)} disabled={index === 0} title="Move Up">
                    <i className="fa fa-arrow-up"></i>
                </button>
                <button className="btn btn-sm btn-light me-1" onClick={() => moveTrackDown(index)} disabled={index === tracks.length - 1} title="Move Down">
                    <i className="fa fa-arrow-down"></i>
                </button>
                <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingTrack(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteTrack(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'Day', selector: (row: Track) => row.day, sortable: true },
        { name: 'Name', selector: (row: Track) => row.name, sortable: true }
    ];

    const allSessions = tracks.flatMap(track => track.sessionsOrder.map(sessionId => {
        const session = track.sessions[sessionId];
        if (!session) return null;
        return { trackId: track.id, trackName: track.name, sessionId, session };
    }).filter(s => s !== null));

    const sessionColumns = [
        { 
            name: 'Actions', 
            cell: (row: any) => {
                const track = tracks.find(t => t.id === row.trackId);
                const sessIndex = track ? track.sessionsOrder.indexOf(row.sessionId) : -1;
                const canMoveUp = sessIndex > 0;
                const canMoveDown = sessIndex < (track ? track.sessionsOrder.length - 1 : -1);
                return (
                    <div>
                        <button className="btn btn-sm btn-light me-1" onClick={() => moveSessionUp(row.trackId, row.sessionId)} disabled={!canMoveUp} title="Move Up">
                            <i className="fa fa-arrow-up"></i>
                        </button>
                        <button className="btn btn-sm btn-light me-1" onClick={() => moveSessionDown(row.trackId, row.sessionId)} disabled={!canMoveDown} title="Move Down">
                            <i className="fa fa-arrow-down"></i>
                        </button>
                        <button className="btn btn-sm btn-warning me-1" onClick={() => {setEditingSession({ trackId: row.trackId, sessionId: row.sessionId, session: row.session })}} title="Edit">
                            <i className="fa fa-edit"></i>
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteSession(row.trackId, row.sessionId)} title="Delete">
                            <i className="fa fa-trash"></i>
                        </button>
                    </div>
                );
            },
            maxWidth: '220px'
        },
        { 
            name: 'Track', 
            selector: (row: any) => row.trackName, 
            sortable: true,
            cell: (row: any) => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '200px' }}>
                    {row.trackName}
                </div>
            )
        },
        { 
            name: 'Name', 
            selector: (row: any) => row.session.name, 
            sortable: true,
            cell: (row: any) => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '200px' }}>
                    {row.session.name}
                </div>
            )
        },
        { 
            name: 'Topic', 
            selector: (row: any) => row.session.topic, 
            sortable: true,
            cell: (row: any) => (
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                    {row.session.topic}
                </div>
            )
        },
        { 
            name: 'Time', 
            selector: (row: any) => row.session.time, 
            sortable: true,
            cell: (row: any) => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {row.session.time}
                </div>
            )
        },
        { 
            name: 'Speakers', 
            cell: (row: any) => {
                const speakers = row.session.speakers ? Object.values(row.session.speakers) : [];
                if (speakers.length === 0) return '';
                
                return (
                    <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '200px' }}>
                        {speakers.map((speaker: any, index: number) => (
                            <span key={speaker.emailid}>
                                <a 
                                    href="#" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setModalSpeakers(speakers);
                                        setShowSpeakerModal(true);
                                    }}
                                    style={{ textDecoration: 'none', color: '#007bff' }}
                                >
                                    {speaker.speakername}
                                </a>
                                {index < speakers.length - 1 && ', '}
                            </span>
                        ))}
                    </div>
                );
            }, 
            sortable: false 
        }
    ];

    const speakerColumns = [
        { name: 'Speaker Name', selector: (row: any) => row.name, sortable: true },
        { name: 'Email', selector: (row: any) => row.mailid, sortable: true },
        { name: 'Company', selector: (row: any) => row.company, sortable: true },
    ];

    const isSpeakerSelected = useCallback((row: any) => {
        return preSelectedSpeakerEmails.includes(row.mailid);
    }, [preSelectedSpeakerEmails]);

    return (
        <>
         <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms"><span className="fa-stack"><i className="fa rhex fa-stack-2x"></i><i className="fa fa-user fa-stack-1x"></i></span></span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Schedule</h3>
                    </div> {/* <!--sec-title end--> */}
                    <div className="register-sec-details">
                      <form onSubmit={(e) => { e.preventDefault()}}>
       

            {/* Add Track */}
            <fieldset className="form-group p-3">
                <legend className="w-auto px-2 subheading">{editingTrack ? 'Edit Track' : 'Add New Track'}</legend>
                <div className="row pt-2">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <div className="row g-2">
                            <div className="col-md-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Track Name"
                                    value={newTrackName}
                                    onChange={(e) => setNewTrackName(e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <select 
                                    className="form-control" 
                                    value={selectedDay} 
                                    onChange={(e) => setSelectedDay(e.target.value)}
                                >
                                    <option value="">Select Day</option>
                                    {Array.from({ length: 10 }, (_, i) => (
                                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <button className="btn btn-primary" onClick={handleAddTrack} disabled={loading || !selectedDay}>
                                    {editingTrack ? 'Update Track' : 'Add Track'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                </button>
                                {editingTrack && (
                                    <button className="btn btn-secondary ms-2" onClick={handleCancelTrack}>
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>

            {/* Tracks Table */}
            <fieldset className="form-group p-3">
                <legend className="w-auto px-2 subheading">Tracks</legend>
                <div className="row pt-2">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <DataTable 
                            columns={trackColumns} 
                            data={tracks} 
                            customStyles={{
                                cells: {
                                    style: {
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        padding: '8px'
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </fieldset>

            {/* Add Session */}
            <fieldset className="form-group p-3">
                <legend className="w-auto px-2 subheading">{editingSession ? 'Edit Session' : 'Add New Session'}</legend>
                <div className="row pt-2">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <div className="row g-2">
                            <div className="col-md-2">
                                <select className="form-control" value={selectedTrackId} onChange={(e) => setSelectedTrackId(e.target.value)} disabled={editingSession ? true : false}>
                                    <option value="">Select Track</option>
                                    {tracks.map(track => <option key={track.id} value={track.id}>{track.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Session Name"
                                    value={newSession.name || ''}
                                    onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
                                />
                            </div>
                            <div className="col-md-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Time"
                                    value={newSession.time || ''}
                                    onChange={(e) => setNewSession({ ...newSession, time: e.target.value })}
                                />
                            </div>
                            <div className="col-md-2">
                                <button style={{marginTop:'0px'}} className="btn btn-primary" onClick={(e) => { e.preventDefault(); handleAddSession(); }} disabled={loading || (!selectedTrackId && !editingSession)}>
                                    {editingSession ? 'Update Session' : 'Add Session'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                </button>
                                {editingSession && (
                                    <button className="btn btn-secondary ms-2" onClick={handleCancelSession}>
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="row">
                           
                            <div className="col">
                                <ReactQuill 
                                    value={newSession.topic || ''}
                                    onChange={(value) => {console.log('ReactQuill onChange - newSession:', newSession, 'new value:', value); setNewSession(prev => ({ ...prev, topic: value }))}}
                                    placeholder="Topic"
                                    theme="snow"
                                    style={{ height: '300px',  background: "white", marginTop:'10px', marginBottom:'10px' }}
                                />
                            </div>
                        </div>
                        <div className="mt-3">
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Select Speakers</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            key={speakerTableKey}
                                            columns={speakerColumns}
                                            data={speakers}
                                            selectableRows
                                            onSelectedRowsChange={(state) => setSelectedSpeakers(state.selectedRows)}
                                            selectableRowSelected={isSpeakerSelected}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                </div>
            </fieldset>

            {/* Sessions Table */}
            <fieldset className="form-group p-3">
                <legend className="w-auto px-2 subheading">Sessions</legend>
                <div className="row pt-2">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <DataTable 
                            columns={sessionColumns} 
                            data={allSessions} 
                            customStyles={{
                                table: {
                                    style: {
                                        fontSize: '12px',
                                        minHeight: '200px',
                                        tableLayout: 'fixed',
                                        width: '100%'
                                    }
                                },
                                head: {
                                    style: {
                                        fontSize: '12px',
                                        minHeight: '30px',
                                        padding: '4px 8px'
                                    }
                                },
                                headCells: {
                                    style: {
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        padding: '4px 8px',
                                        maxWidth: '50px',
                                        width: '50px',
                                        flex: '0 0 50px'
                                    }
                                },
                                cells: {
                                    style: {
                                        fontSize: '12px',
                                        padding: '4px 8px',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        maxWidth: '50px',
                                        width: '50px',
                                        flex: '0 0 50px'
                                    }
                                },
                                rows: {
                                    style: {
                                        minHeight: '35px'
                                    }
                                }
                            }}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[5, 10, 15, 20]}
                        />
                    </div>
                </div>
            </fieldset>

            {/* Speaker Details Modal */}
            <div className={`modal ${showSpeakerModal ? 'show' : ''}`} style={{ display: showSpeakerModal ? 'block' : 'none' }} tabIndex={-1}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Speaker Details</h5>
                            <button type="button" className="btn-close" onClick={() => setShowSpeakerModal(false)}></button>
                        </div>
                        <div className="modal-body">
                            {modalSpeakers.length > 0 ? (
                                <div className="row">
                                    {modalSpeakers.map((speaker: any, index: number) => (
                                        <div key={speaker.emailid} className="col-md-6 mb-3">
                                            <div className="card">
                                                <div className="card-body">
                                                    <h6 className="card-title">{speaker.speakername}</h6>
                                                    <p className="card-text mb-1"><strong>Email:</strong> {speaker.emailid}</p>
                                                    <p className="card-text mb-1"><strong>Order:</strong> {speaker.order}</p>
                                                    {speaker.company && <p className="card-text mb-0"><strong>Company:</strong> {speaker.company}</p>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No speakers found.</p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowSpeakerModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
            {showSpeakerModal && <div className="modal-backdrop show"></div>}
       
          
                        </form>


                    </div> {/* <!--register-sec-details end--> */}
                </div>
            </section>
        </>
    );
};

export default ManageSchedule;